import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ROOM_TYPES,
  ROOM_TYPE_LABELS,
  SEATING_CONFIG_TYPES,
  SEATING_CONFIG_LABELS,
  SEATING_CONFIG_DESCRIPTIONS,
  SEATING_CONFIG_SUGGESTIONS,
  type RoomType,
  type SeatingConfigType,
  type CreateVenueRoomDto,
  type CreateSeatingConfigDto,
  type VenueRoomWithConfigs,
} from '@/types/venue-room.types';

interface VenueRoomFormProps {
  room?: VenueRoomWithConfigs | null;
  onSubmit: (data: CreateVenueRoomDto) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const VenueRoomForm: React.FC<VenueRoomFormProps> = ({
  room,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const { t } = useTranslation('services');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [roomType, setRoomType] = useState<RoomType>(ROOM_TYPES.ROOM);
  const [maxCapacity, setMaxCapacity] = useState<number | undefined>();
  const [floorAreaSqft, setFloorAreaSqft] = useState<number | undefined>();
  const [hasAvEquipment, setHasAvEquipment] = useState(false);
  const [hasProjector, setHasProjector] = useState(false);
  const [hasScreen, setHasScreen] = useState(false);
  const [hasWhiteboard, setHasWhiteboard] = useState(false);
  const [hasVideoConferencing, setHasVideoConferencing] = useState(false);
  const [hasNaturalLight, setHasNaturalLight] = useState(false);
  const [hasStage, setHasStage] = useState(false);
  const [hasDanceFloor, setHasDanceFloor] = useState(false);
  const [isAccessible, setIsAccessible] = useState(false);
  const [seatingConfigs, setSeatingConfigs] = useState<CreateSeatingConfigDto[]>([]);
  const [showConfigPicker, setShowConfigPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (room) {
      setName(room.name);
      setDescription(room.description || '');
      setRoomType(room.room_type);
      setMaxCapacity(room.max_capacity ?? undefined);
      setFloorAreaSqft(room.floor_area_sqft ?? undefined);
      setHasAvEquipment(room.has_av_equipment);
      setHasProjector(room.has_projector);
      setHasScreen(room.has_screen);
      setHasWhiteboard(room.has_whiteboard);
      setHasVideoConferencing(room.has_video_conferencing);
      setHasNaturalLight(room.has_natural_light);
      setHasStage(room.has_stage);
      setHasDanceFloor(room.has_dance_floor);
      setIsAccessible(room.is_accessible);
      setSeatingConfigs(
        room.seating_configs.map(sc => ({
          config_type: sc.config_type,
          capacity: sc.capacity ?? undefined,
          notes: sc.notes ?? undefined,
          is_default: sc.is_default
        }))
      );
    }
  }, [room]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('venueRoom.errors.nameRequired'));
      return;
    }
    if (seatingConfigs.length === 0) {
      setError(t('venueRoom.errors.seatingRequired'));
      setValidationError('At least one seating configuration is required');
      return;
    }
    if (maxCapacity) {
      const overCapacity = seatingConfigs.filter(sc => sc.capacity && sc.capacity > maxCapacity);
      if (overCapacity.length > 0) {
        const names = overCapacity.map(sc => SEATING_CONFIG_LABELS[sc.config_type]).join(', ');
        setError(`⚠️ ${names} capacity exceeds room max capacity of ${maxCapacity}`);
        return;
      }
    }
    setError(null);
    setValidationError(null);

    const data: CreateVenueRoomDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      room_type: roomType,
      max_capacity: maxCapacity,
      floor_area_sqft: floorAreaSqft,
      has_av_equipment: hasAvEquipment,
      has_projector: hasProjector,
      has_screen: hasScreen,
      has_whiteboard: hasWhiteboard,
      has_video_conferencing: hasVideoConferencing,
      has_natural_light: hasNaturalLight,
      has_stage: hasStage,
      has_dance_floor: hasDanceFloor,
      is_accessible: isAccessible,
      seating_configs: seatingConfigs.length > 0 ? seatingConfigs : undefined
    };

    await onSubmit(data);
  };

  const addSeatingConfig = (configType: SeatingConfigType) => {
    if (seatingConfigs.some(sc => sc.config_type === configType)) return;
    setSeatingConfigs(prev => [
      ...prev,
      { config_type: configType, capacity: maxCapacity || undefined, is_default: prev.length === 0 }
    ]);
    setValidationError(null);
  };

  const toggleSeatingConfig = (configType: SeatingConfigType) => {
    if (seatingConfigs.some(sc => sc.config_type === configType)) {
      removeSeatingConfig(configType);
    } else {
      addSeatingConfig(configType);
    }
  };

  const removeSeatingConfig = (configType: SeatingConfigType) => {
    setSeatingConfigs(prev => {
      const updated = prev.filter(sc => sc.config_type !== configType);
      // If we removed the default, make the first one default
      if (updated.length > 0 && !updated.some(sc => sc.is_default)) {
        updated[0].is_default = true;
      }
      return updated;
    });
  };

  const updateSeatingConfig = (configType: SeatingConfigType, field: string, value: unknown) => {
    // Clamp capacity to room max capacity
    if (field === 'capacity' && typeof value === 'number' && maxCapacity && value > maxCapacity) {
      value = maxCapacity;
    }
    setSeatingConfigs(prev =>
      prev.map(sc => {
        if (sc.config_type !== configType) return sc;
        if (field === 'is_default' && value === true) {
          // When setting a new default, clear others
          return { ...sc, is_default: true };
        }
        return { ...sc, [field]: value };
      }).map(sc => {
        // Clear is_default for others when one is set
        if (field === 'is_default' && value === true && sc.config_type !== configType) {
          return { ...sc, is_default: false };
        }
        return sc;
      })
    );
  };

  const availableConfigs = Object.values(SEATING_CONFIG_TYPES);

  const handleQuickAdd = () => {
    const suggestions = SEATING_CONFIG_SUGGESTIONS[roomType] || [];
    const roomCap = maxCapacity || room?.max_capacity;
    
    suggestions.forEach((configType, index) => {
      if (!seatingConfigs.some(sc => sc.config_type === configType)) {
        setSeatingConfigs(prev => [
          ...prev,
          {
            config_type: configType,
            capacity: roomCap || undefined,
            is_default: prev.length === 0 && index === 0
          }
        ]);
      }
    });
    setShowConfigPicker(false);
    setValidationError(null);
  };

  const amenities = [
    { key: 'hasAvEquipment', label: t('venueRoom.amenities.avEquipment'), value: hasAvEquipment, setter: setHasAvEquipment },
    { key: 'hasProjector', label: t('venueRoom.amenities.projector'), value: hasProjector, setter: setHasProjector },
    { key: 'hasScreen', label: t('venueRoom.amenities.screen'), value: hasScreen, setter: setHasScreen },
    { key: 'hasWhiteboard', label: t('venueRoom.amenities.whiteboard'), value: hasWhiteboard, setter: setHasWhiteboard },
    { key: 'hasVideoConferencing', label: t('venueRoom.amenities.videoConferencing'), value: hasVideoConferencing, setter: setHasVideoConferencing },
    { key: 'hasNaturalLight', label: t('venueRoom.amenities.naturalLight'), value: hasNaturalLight, setter: setHasNaturalLight },
    { key: 'hasStage', label: t('venueRoom.amenities.stage'), value: hasStage, setter: setHasStage },
    { key: 'hasDanceFloor', label: t('venueRoom.amenities.danceFloor'), value: hasDanceFloor, setter: setHasDanceFloor },
    { key: 'isAccessible', label: t('venueRoom.amenities.accessible'), value: isAccessible, setter: setIsAccessible }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="venue-room-form">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('venueRoom.basicInfo')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="room-name" className="text-sm font-medium">{t('venueRoom.fields.name')}</Label>
            <Input
              id="room-name"
              data-testid="room-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('venueRoom.fields.namePlaceholder')}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="room-type" className="text-sm font-medium">{t('venueRoom.fields.type')}</Label>
            <select
              id="room-type"
              data-testid="room-type-select"
              value={roomType}
              onChange={e => setRoomType(e.target.value as RoomType)}
              className="mt-1 w-full h-9 px-3 text-sm rounded-md border border-input bg-background"
              title="Room type"
            >
              {Object.values(ROOM_TYPES).map(type => (
                <option key={type} value={type}>{ROOM_TYPE_LABELS[type]}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="room-description" className="text-sm font-medium">{t('venueRoom.fields.description')}</Label>
          <textarea
            id="room-description"
            data-testid="room-description-input"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('venueRoom.fields.descriptionPlaceholder')}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring resize-none"
            rows={2}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="max-capacity" className="text-sm font-medium">{t('venueRoom.fields.maxCapacity')}</Label>
            <Input
              id="max-capacity"
              data-testid="room-capacity-input"
              type="number"
              value={maxCapacity ?? ''}
              onChange={e => setMaxCapacity(e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder={t('venueRoom.fields.maxCapacityPlaceholder')}
              min="1"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="floor-area" className="text-sm font-medium">{t('venueRoom.fields.floorArea')}</Label>
            <Input
              id="floor-area"
              type="number"
              value={floorAreaSqft ?? ''}
              onChange={e => setFloorAreaSqft(e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder={t('venueRoom.fields.floorAreaPlaceholder')}
              min="1"
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Amenities */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('venueRoom.amenitiesTitle')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {amenities.map(({ key, label, value, setter }) => (
            <label key={key} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={value}
                onChange={e => setter(e.target.checked)}
                className="h-4 w-4 text-primary rounded border-gray-300"
                data-testid={`amenity-${key}`}
              />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Seating Configurations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('venueRoom.seatingConfigsTitle')}</h3>
            <span className="text-destructive text-sm">*</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowConfigPicker(!showConfigPicker)}
              data-testid="add-seating-config-btn"
            >
              {showConfigPicker ? (
                <><X className="h-3 w-3 mr-1" /> {t('common:actions.close')}</>
              ) : (
                <><Plus className="h-3 w-3 mr-1" /> {t('venueRoom.addConfiguration')}</>
              )}
            </Button>
          </div>
        </div>
        
        {/* Help text */}
        <p className="text-xs text-muted-foreground">
          {t('venueRoom.seatingHint')}
        </p>
        
        {validationError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <span>⚠️</span>
            <span>{validationError}</span>
          </div>
        )}

        {/* Config Picker */}
        {showConfigPicker && (
          <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">{t('venueRoom.selectConfigTypes')}</span>
              <div className="flex items-center gap-1">
                {SEATING_CONFIG_SUGGESTIONS[roomType] && seatingConfigs.length === 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={handleQuickAdd} className="text-xs text-purple-600 hover:text-purple-800">
                    {t('venueRoom.quickAddSuggested')}
                  </Button>
                )}
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowConfigPicker(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
              {availableConfigs.map(ct => {
                const isSelected = seatingConfigs.some(sc => sc.config_type === ct);
                return (
                  <label
                    key={ct}
                    className={`flex items-start gap-2 p-2 text-xs rounded cursor-pointer transition-all border ${
                      isSelected
                        ? 'bg-primary/5 border-primary/30 shadow-sm'
                        : 'border-transparent hover:bg-white hover:shadow-sm hover:border-gray-200'
                    }`}
                    data-testid={`seating-option-${ct}`}
                    title={SEATING_CONFIG_DESCRIPTIONS[ct]}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSeatingConfig(ct)}
                      className="h-4 w-4 mt-0.5 text-primary rounded border-gray-300 flex-shrink-0"
                    />
                    <div>
                      <div className="font-medium">{SEATING_CONFIG_LABELS[ct]}</div>
                      <div className="text-gray-500 truncate">{SEATING_CONFIG_DESCRIPTIONS[ct]}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Added Configs */}
        {seatingConfigs.length > 0 ? (
          <div className="space-y-2">
            {seatingConfigs.map(sc => (
              <div
                key={sc.config_type}
                className="flex items-center gap-3 p-3 border rounded-lg bg-white"
                data-testid={`seating-config-${sc.config_type}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {SEATING_CONFIG_LABELS[sc.config_type]}
                    </span>
                    {sc.is_default && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                        {t('venueRoom.default')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 min-w-[220px]">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={sc.capacity ?? ''}
                      onChange={e => updateSeatingConfig(sc.config_type, 'capacity', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="Capacity"
                      min="1"
                      max={maxCapacity || undefined}
                      className={`w-24 h-8 text-xs ${sc.capacity && maxCapacity && sc.capacity > maxCapacity ? 'border-red-400 focus:ring-red-300' : ''}`}
                      data-testid={`seating-capacity-${sc.config_type}`}
                      title={maxCapacity ? `Max: ${maxCapacity}` : undefined}
                    />
                    {!sc.is_default && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateSeatingConfig(sc.config_type, 'is_default', true)}
                        className="text-xs h-8 px-2"
                      >
                        {t('venueRoom.setDefault')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => removeSeatingConfig(sc.config_type)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-seating-${sc.config_type}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-amber-600 text-lg">⚠️</span>
            <div className="text-xs text-amber-700">
              <p className="font-medium mb-1">{t('venueRoom.seatingRequired')}</p>
              <p>{t('venueRoom.seatingRequiredHint')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" disabled={loading} data-testid="save-room-btn">
          {loading && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />}
          <Save className="h-3 w-3 mr-1" />
          {room ? t('venueRoom.updateRoom') : t('venueRoom.createRoom')}
        </Button>
      </div>
    </form>
  );
};
